import { Injectable } from '@angular/core';
import { DryEyeMedicalHistoryFormGroupValue } from '../dry-eye-medical-history-form.model';
import {
  DrugEntry,
  MedicalHistoryDrugParserService,
  MedicationTierGroup
} from '../medical-history-drug-parser.service';
import {
  ALLERGY_FACTOR_KEYS,
  ClinicalContextGroups,
  ClinicalContextItem,
  MedicalHistoryRiskSummary,
  MedicalHistoryRiskSummaryWithDetail,
  RiskFactor,
  RiskSeverity
} from '../medical-history-risk-factor.schema';

// ── Public types — consumed by MedicalHistoryQuickScanComponent ──────────────

export const GROUP_ORDER = ['conditions', 'symptoms', 'medications', 'lifestyle', 'ocular', 'environmental', 'other'] as const;
export type CategoryKey = typeof GROUP_ORDER[number];

export interface RiskFactorVM extends RiskFactor {
  drugEntries?: DrugEntry[];
  medicationTiers?: MedicationTierGroup[];
}

export interface PreviewItem {
  label: string;
  severity: 'high' | 'moderate' | 'low';
  count?: number;
}

export interface CategorySection {
  key: string;
  label: string;
  flaggedCount: number;
  highestSeverity: RiskSeverity;
  previewItems: PreviewItem[];
  previewTail: string;
  itemsBySeverity: Record<RiskSeverity, RiskFactorVM[]>;
  allergyItemsBySeverity: Record<RiskSeverity, RiskFactorVM[]>;
  hasNonAllergyItems: boolean;
  hasAllergyItems: boolean;
}

export interface QuickScanViewModel {
  hasAnyRisk: boolean;
  totalCount: number;
  highCount: number;
  moderateCount: number;
  lowCount: number;
  countBySeverity: Record<RiskSeverity, number>;
  categories: CategorySection[];
  clinicalContextPrimary: ClinicalContextItem[];
  clinicalContextFitzpatrick: ClinicalContextItem[];
  hasClinicalContext: boolean;
  chiefComplaint: string;
}

export const EMPTY_VIEW_MODEL: QuickScanViewModel = {
  hasAnyRisk: false,
  totalCount: 0,
  highCount: 0,
  moderateCount: 0,
  lowCount: 0,
  countBySeverity: { high: 0, moderate: 0, low: 0 },
  categories: [],
  clinicalContextPrimary: [],
  clinicalContextFitzpatrick: [],
  hasClinicalContext: false,
  chiefComplaint: ''
};

// ── Internal grouping types ─────────────────────────────────────────────────

interface RiskFactorGroup {
  groupKey: string;
  label: string;
  items: RiskFactorVM[];
}

interface GroupedSeverity {
  groups: RiskFactorGroup[];
  count: number;
}

interface GroupedMedicalHistorySummary {
  high: GroupedSeverity;
  moderate: GroupedSeverity;
  low: GroupedSeverity;
  hasAnyRisk: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────

const PREVIEW_CHIP_LIMIT = 3;

const ALLERGY_KEYS = new Set<string>(ALLERGY_FACTOR_KEYS);

const GROUP_LABELS: Record<string, string> = {
  conditions:    'Conditions',
  symptoms:      'Symptoms',
  medications:   'Medications',
  lifestyle:     'Lifestyle',
  ocular:        'Post ocular history',
  environmental: 'Environmental',
  other:         'Other',
};

const CATEGORY_DISPLAY_LABELS: Record<string, string> = {
  conditions:    'Conditions & Allergies',
  symptoms:      'Symptoms',
  medications:   'Medications',
  lifestyle:     'Lifestyle & Environment',
  ocular:        'Ocular History',
  environmental: 'Environmental',
  other:         'Other',
};

const SEVERITY_RANK: Record<RiskSeverity, number> = { low: 0, moderate: 1, high: 2 };
const SEVERITY_ORDER: ReadonlyArray<RiskSeverity> = ['high', 'moderate', 'low'];

/**
 * Pure view-model construction for the medical-history quick-scan UI. Takes a
 * `MedicalHistoryRiskSummaryWithDetail` and the current form value, returns a
 * `QuickScanViewModel` suitable for the template's async pipe. Stateless and
 * idempotent — the only collaborator is `MedicalHistoryDrugParserService` to
 * convert the medication-card detail string into structured drug entries.
 */
@Injectable({ providedIn: 'root' })
export class MedicalHistoryQuickScanViewModelBuilder {
  constructor(private drugParser: MedicalHistoryDrugParserService) {}

  build(
    summary: MedicalHistoryRiskSummaryWithDetail,
    formValue: DryEyeMedicalHistoryFormGroupValue,
    excludeCategoriesSet: ReadonlySet<CategoryKey>
  ): QuickScanViewModel {
    const grouped = this.buildGroupedSummary(summary);
    const baseViewModel = this.buildViewModel(grouped, summary.clinicalContext, excludeCategoriesSet);
    /* `historyOfPresentingIllness` carries the patient-filled CDERF answer that
       surfaces under the renamed "Patient's Concerns" section — it is not the
       clinician-edited chief complaint despite the field name. Keep the VM key
       `chiefComplaint` for template stability. */
    const rawComplaint = formValue && formValue.historyOfPresentingIllness;
    const chiefComplaint = typeof rawComplaint === 'string' ? rawComplaint.trim() : '';
    return { ...baseViewModel, chiefComplaint };
  }

  private toRiskFactorViewModel(riskFactor: RiskFactor): RiskFactorVM {
    if (riskFactor.detail) {
      const drugEntries = this.drugParser.parseDrugDetail(riskFactor.detail);
      const isPatientEntered = riskFactor.key === 'patientEnteredMedicationsFreeText';
      return {
        ...riskFactor,
        drugEntries,
        medicationTiers: isPatientEntered ? [] : this.drugParser.buildMedicationTiers(drugEntries)
      };
    }
    return riskFactor;
  }

  private buildViewModel(
    grouped: GroupedMedicalHistorySummary,
    clinicalContext: ClinicalContextGroups,
    excludeCategoriesSet: ReadonlySet<CategoryKey>
  ): Omit<QuickScanViewModel, 'chiefComplaint'> {
    const categoryMap: Record<string, CategorySection> = {};

    const distributeGroupsIntoCategories = (groups: RiskFactorGroup[], severity: RiskSeverity) => {
      groups.forEach(group => {
        if (!categoryMap[group.groupKey]) {
          categoryMap[group.groupKey] = {
            key: group.groupKey,
            label: CATEGORY_DISPLAY_LABELS[group.groupKey] || group.label,
            flaggedCount: 0,
            highestSeverity: severity,
            previewItems: [],
            previewTail: '',
            itemsBySeverity:        { high: [], moderate: [], low: [] },
            allergyItemsBySeverity: { high: [], moderate: [], low: [] },
            hasNonAllergyItems: false,
            hasAllergyItems: false
          };
        }
        const categorySection = categoryMap[group.groupKey];
        const viewModels = group.items.map(riskFactor => this.toRiskFactorViewModel(riskFactor));
        const isConditions = group.groupKey === 'conditions';
        const allergyItems    = isConditions ? viewModels.filter(riskFactor => ALLERGY_KEYS.has(riskFactor.key)) : [];
        const nonAllergyItems = isConditions ? viewModels.filter(riskFactor => !ALLERGY_KEYS.has(riskFactor.key)) : viewModels;

        categorySection.itemsBySeverity[severity].push(...nonAllergyItems);
        categorySection.allergyItemsBySeverity[severity].push(...allergyItems);

        if (SEVERITY_RANK[severity] > SEVERITY_RANK[categorySection.highestSeverity]) {
          categorySection.highestSeverity = severity;
        }
      });
    };

    distributeGroupsIntoCategories(grouped.high.groups, 'high');
    distributeGroupsIntoCategories(grouped.moderate.groups, 'moderate');
    distributeGroupsIntoCategories(grouped.low.groups, 'low');

    const toPreviewItem = (riskFactor: RiskFactorVM, severity: RiskSeverity): PreviewItem => {
      const previewItem: PreviewItem = { label: riskFactor.label, severity };
      const isMedicationCount = riskFactor.key === 'medications' || riskFactor.key === 'patientEnteredMedicationsFreeText';
      if (isMedicationCount && riskFactor.drugEntries) {
        previewItem.count = riskFactor.drugEntries.length;
      }
      return previewItem;
    };

    Object.values(categoryMap).forEach(categorySection => {
      // Total flag count = sum of every severity bucket across both groups.
      categorySection.flaggedCount = SEVERITY_ORDER.reduce((runningTotal, severity) =>
        runningTotal
          + categorySection.itemsBySeverity[severity].length
          + categorySection.allergyItemsBySeverity[severity].length,
        0
      );

      categorySection.hasNonAllergyItems = SEVERITY_ORDER.some(severity => categorySection.itemsBySeverity[severity].length > 0);
      categorySection.hasAllergyItems    = SEVERITY_ORDER.some(severity => categorySection.allergyItemsBySeverity[severity].length > 0);

      // Preview items: stable severity order (high → moderate → low), conditions
      // bucket first then allergy bucket within each severity.
      const previewItemsBySeverity: PreviewItem[] = SEVERITY_ORDER.reduce<PreviewItem[]>((accumulatedItems, severity) => {
        const itemsForThisSeverity = [
          ...categorySection.itemsBySeverity[severity],
          ...categorySection.allergyItemsBySeverity[severity]
        ];
        return accumulatedItems.concat(itemsForThisSeverity.map(riskFactor => toPreviewItem(riskFactor, severity)));
      }, []);

      categorySection.previewItems = previewItemsBySeverity.slice(0, PREVIEW_CHIP_LIMIT);

      const overflowItems = previewItemsBySeverity.slice(PREVIEW_CHIP_LIMIT);
      const overflowSummaryParts = SEVERITY_ORDER
        .map(severity => ({ severity, count: overflowItems.filter(previewItem => previewItem.severity === severity).length }))
        .filter(({ count }) => count > 0)
        .map(({ severity, count }) => `+${count} ${severity}`);
      categorySection.previewTail = overflowSummaryParts.join(', ');
    });

    const categories = GROUP_ORDER
      .filter(groupKey => categoryMap[groupKey] && !excludeCategoriesSet.has(groupKey))
      .map(groupKey => categoryMap[groupKey]);

    const totalCount   = grouped.high.count + grouped.moderate.count + grouped.low.count;
    const highCount     = grouped.high.count;
    const moderateCount = grouped.moderate.count;
    const lowCount      = grouped.low.count;

    const clinicalContextPrimary = clinicalContext.primary;
    const clinicalContextFitzpatrick = clinicalContext.fitzpatrick;
    const hasClinicalContext = clinicalContextPrimary.length > 0 || clinicalContextFitzpatrick.length > 0;

    return {
      hasAnyRisk: grouped.hasAnyRisk,
      totalCount,
      highCount,
      moderateCount,
      lowCount,
      countBySeverity: { high: highCount, moderate: moderateCount, low: lowCount },
      categories,
      clinicalContextPrimary,
      clinicalContextFitzpatrick,
      hasClinicalContext
    };
  }

  // ── Grouping helpers ─────────────────────────────────────────────────────

  private mapGroupKey(medicalHistoryRiskFactor: RiskFactor): string {
    if (medicalHistoryRiskFactor.group === 'medications' || medicalHistoryRiskFactor.key === 'patientEnteredMedicationsFreeText') { return 'medications'; }
    if (medicalHistoryRiskFactor.group === 'checkboxConditions' || medicalHistoryRiskFactor.group === 'medicalConditions') { return 'conditions'; }
    if (medicalHistoryRiskFactor.group === 'slider' || medicalHistoryRiskFactor.group === 'radioCondition' || medicalHistoryRiskFactor.group === 'symptomDuration') { return 'symptoms'; }
    if (medicalHistoryRiskFactor.group === 'lifestyle') { return 'lifestyle'; }
    if (medicalHistoryRiskFactor.group === 'freeTextPresent') { return 'ocular'; }
    return 'other';
  }

  private groupItems(items: RiskFactor[]): GroupedSeverity {
    const buckets: Record<string, RiskFactorVM[]> = {};
    items.forEach(riskFactor => {
      const groupKey = this.mapGroupKey(riskFactor);
      if (!buckets[groupKey]) { buckets[groupKey] = []; }
      buckets[groupKey].push(riskFactor);
    });

    const groups: RiskFactorGroup[] = GROUP_ORDER
      .filter(groupKey => buckets[groupKey] && buckets[groupKey].length > 0)
      .map(groupKey => ({ groupKey, label: GROUP_LABELS[groupKey], items: buckets[groupKey] }));

    return { groups, count: items.length };
  }

  private buildGroupedSummary(summary: MedicalHistoryRiskSummary): GroupedMedicalHistorySummary {
    if (!summary) {
      return { high: { groups: [], count: 0 }, moderate: { groups: [], count: 0 }, low: { groups: [], count: 0 }, hasAnyRisk: false };
    }
    return {
      high:       this.groupItems(summary.high   || []),
      moderate:   this.groupItems(summary.moderate || []),
      low:        this.groupItems(summary.low     || []),
      hasAnyRisk: summary.hasAnyRisk
    };
  }
}
